import { Button } from '@dfh-corp-sites/ui'
import React from 'react'

import { isChunkLoadError, reloadOnceForStaleChunk } from '../../utils/chunkReload'

interface ChunkErrorBoundaryProps {
  children: React.ReactNode
}

interface ChunkErrorBoundaryState {
  hasError: boolean
  error: unknown
  // 'hidden' while a reload is in flight (or before the decision is known). Otherwise the recovery
  // declined to reload and nothing else will repair the subtree, so render a retry fallback — the
  // variant carries why, which decides both the copy and whether a reconnect can recover.
  fallback: 'hidden' | 'transient' | 'persistent'
}

/**
 * Catches render-time chunk-load failures — e.g. a `React.lazy` import whose chunk was removed by a
 * redeploy — and hard-reloads once to pick up the current chunk map. When the recovery skips the
 * reload (transient network failure or loop guard), renders a retry fallback; for a transient failure
 * it also reloads once connectivity returns. Any non-chunk error is rethrown from `render` so it still
 * propagates to Sentry via the normal unhandled path (unchanged behaviour).
 */
export class ChunkErrorBoundary extends React.Component<
  ChunkErrorBoundaryProps,
  ChunkErrorBoundaryState
> {
  override state: ChunkErrorBoundaryState = {
    hasError: false,
    error: undefined,
    fallback: 'hidden',
  }

  static getDerivedStateFromError(error: unknown): Partial<ChunkErrorBoundaryState> {
    // `fallback` stays 'hidden' until `componentDidCatch` learns the reload decision, so the first
    // post-error render shows nothing — no fallback flash when a reload is about to fire.
    return { hasError: true, error }
  }

  // Bound per instance so an unmounting boundary can only ever remove its own listener.
  private readonly reloadPage = (): void => {
    window.location.reload()
  }

  override componentDidCatch(error: unknown): void {
    if (!isChunkLoadError(error)) return

    const decision = reloadOnceForStaleChunk(error)
    if (decision === 'reload-skipped-transient') {
      this.setState({ fallback: 'transient' })
      // The connection is what broke, so once it returns a reload can recover.
      window.addEventListener('online', this.reloadPage, { once: true })
    } else if (decision === 'reload-skipped') {
      // The loop guard fired while online: a reload already failed to recover the chunk, and waiting
      // for an 'online' event that cannot arrive would be a dead affordance. Only the manual retry
      // (or the cooldown elapsing) helps here.
      this.setState({ fallback: 'persistent' })
    }
  }

  override componentWillUnmount(): void {
    window.removeEventListener('online', this.reloadPage)
  }

  override render(): React.ReactNode {
    const { hasError, error, fallback } = this.state
    if (hasError) {
      if (isChunkLoadError(error)) {
        // Either a reload is in flight (render nothing rather than the broken subtree) or the
        // reload was skipped and the user needs a way to retry.
        // `role="alert"` because this replaces the whole page element (header and footer included),
        // so nothing else announces the failure to a screen reader.
        return fallback !== 'hidden' ? (
          <div
            role="alert"
            className="flex min-h-[50vh] flex-col items-center justify-center gap-4 p-8 text-center"
          >
            <p className="text-lg font-medium text-brand-navy">This page failed to load.</p>
            <p className="text-sm">
              {fallback === 'transient'
                ? 'Check your connection and try again.'
                : 'Reloading it did not help. Try again in a moment.'}
            </p>
            <div className="flex flex-col items-center gap-2 sm:flex-row sm:gap-4">
              <Button type="button" variant="secondary" size="sm" onClick={this.reloadPage}>
                Try again
              </Button>
              {/* A plain anchor, not gatsby-link: client-side navigation reuses the broken chunk map
                  and this boundary never resets, so the way out has to be a fresh document load. */}
              <Button asChild variant="text" size="sm">
                <a href="/">Go to the homepage</a>
              </Button>
            </div>
          </div>
        ) : null
      }
      // Not a chunk error: let it propagate to a parent boundary / window.onerror (→ Sentry).
      throw error
    }
    return this.props.children
  }
}
